import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { CheckCircle, Package, Clock, MapPin } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { formatDeliveryTime, formatEstimatedArrival } from '../utils/deliveryTime';

const OrderConfirmationPage = () => {
  const navigate = useNavigate();
  const [order, setOrder] = useState(null);

  useEffect(() => {
    const savedOrder = localStorage.getItem('tribaldash_last_order');
    if (savedOrder) {
      setOrder(JSON.parse(savedOrder));
    } else {
      navigate('/menu');
    }
  }, [navigate]);

  if (!order) {
    return null;
  }

  const isCurbside = order.delivery?.fulfillmentType === 'curbside';
  const deliveryDateObj = new Date(order.delivery.date);
  const formattedDate = deliveryDateObj.toLocaleDateString('en-US', {
    weekday: 'long',
    year: 'numeric',
    month: 'long',
    day: 'numeric'
  });

  return (
    <div className="min-h-screen bg-gray-50 py-16">
      <div className="container mx-auto px-4 max-w-3xl">
        {/* Success Icon */}
        <div className="text-center mb-8">
          <div className="inline-flex items-center justify-center w-20 h-20 bg-green-100 rounded-full mb-4">
            <CheckCircle className="w-12 h-12 text-green-600" />
          </div>
          <h1 className="text-4xl font-bold text-neutral-900 mb-2">
            Order Confirmed!
          </h1>
          <p className="text-gray-600 text-lg">
            {isCurbside
              ? "Thank you for your order. We'll have it ready for curbside pickup!"
              : "Thank you for your order. We'll have it delivered to you soon!"}
          </p>
        </div>

        {/* Order Details */}
        <Card className="mb-6">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Package className="w-5 h-5" />
              Order Details
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-4">
              <div className="flex justify-between items-center pb-4 border-b">
                <span className="text-gray-600">Order Number:</span>
                <span className="font-bold text-neutral-900">{order.id}</span>
              </div>
              
              <div className="space-y-3">
                {order.items.map((item) => (
                  <div key={item.id} className="flex items-center gap-4">
                    <img
                      src={item.image}
                      alt={item.name}
                      className="w-16 h-16 object-cover rounded"
                    />
                    <div className="flex-1">
                      <h4 className="font-semibold text-neutral-900">{item.name}</h4>
                      <p className="text-sm text-gray-600">
                        Quantity: {item.quantity} × ${item.price}
                      </p>
                    </div>
                    <span className="font-bold text-neutral-900">
                      ${(item.price * item.quantity).toFixed(2)}
                    </span>
                  </div>
                ))}
              </div>

              <div className="border-t pt-4 space-y-2">
                <div className="flex justify-between text-gray-700">
                  <span>Subtotal:</span>
                  <span>${order.subtotal.toFixed(2)}</span>
                </div>
                <div className="flex justify-between text-gray-700">
                  <span>{isCurbside ? 'Pickup Fee:' : 'Delivery Fee:'}</span>
                  <span>{order.deliveryFee === 0 ? 'FREE' : `$${order.deliveryFee.toFixed(2)}`}</span>
                </div>
                <div className="flex justify-between text-gray-700">
                  <span>Tax:</span>
                  <span>{order.tax === 0 ? 'Exempt' : `$${order.tax.toFixed(2)}`}</span>
                </div>
                <div className="flex justify-between text-lg font-bold text-neutral-900 pt-2 border-t">
                  <span>Total:</span>
                  <span>${order.total.toFixed(2)}</span>
                </div>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Delivery / Curbside Information */}
        <Card className="mb-6">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Clock className="w-5 h-5" />
              {isCurbside ? 'Curbside Pickup' : 'Delivery Information'}
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-4">
              <div>
                <p className="text-sm text-gray-600 mb-1">
                  {isCurbside ? 'Pickup Window:' : 'Estimated Delivery:'}
                </p>
                <p className="font-semibold text-neutral-900">
                  {formattedDate}
                </p>
                <p className="text-gray-700">{formatDeliveryTime(order.delivery)}</p>
                {order.delivery.estimatedArrival && (
                  <p className="text-sm text-neutral-600 mt-1">
                    {isCurbside ? 'Ready from: ' : 'Estimated arrival: '}
                    {formatEstimatedArrival(order.delivery.estimatedArrival)}
                  </p>
                )}
              </div>
              
              <div>
                <p className="text-sm text-gray-600 mb-1 flex items-center gap-2">
                  <MapPin className="w-4 h-4" />
                  {isCurbside ? 'Pickup Location:' : 'Delivery Address:'}
                </p>
                <p className="font-semibold text-neutral-900">{order.delivery.address}</p>
              </div>
              
              {order.delivery.notes && (
                <div>
                  <p className="text-sm text-gray-600 mb-1">
                    {isCurbside ? 'Pickup Notes:' : 'Delivery Notes:'}
                  </p>
                  <p className="text-gray-700">{order.delivery.notes}</p>
                </div>
              )}
            </div>
          </CardContent>
        </Card>

        {/* Payment Information */}
        <Card className="mb-8">
          <CardHeader>
            <CardTitle>Payment Information</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-2">
              <p className="text-gray-700">
                <span className="text-gray-600">Payment method:</span> Debit card ending in{' '}
                {order.payment.lastFour ||
                  (order.payment.cardNumber ? order.payment.cardNumber.slice(-4) : '****')}
              </p>
              <p className="text-gray-700">
                <span className="text-gray-600">Cardholder:</span> {order.payment.cardName}
              </p>
              {order.payment.transactionId && (
                <p className="text-gray-700">
                  <span className="text-gray-600">Transaction ID:</span> {order.payment.transactionId}
                </p>
              )}
              <div className={`border rounded-lg p-4 mt-4 ${
                order.payment.paymentStatus === 'pending_manual'
                  ? 'bg-amber-50 border-amber-200'
                  : 'bg-green-50 border-green-200'
              }`}>
                <p className={`text-sm ${
                  order.payment.paymentStatus === 'pending_manual'
                    ? 'text-amber-900'
                    : 'text-green-800'
                }`}>
                  {order.payment.paymentStatus === 'pending_manual' ? (
                    <>
                      Your order is confirmed. Payment will be collected by Cloud 9{' '}
                      {isCurbside ? 'at curbside pickup' : 'at delivery'}{' '}
                      (debit card ending in {order.payment.lastFour || '****'}).
                    </>
                  ) : (
                    <>
                      ✓ Your debit card payment was processed through ProteusERP (Cloud 9).{' '}
                      {order.payment.paymentStatus === 'approved'
                        ? 'Payment approved.'
                        : 'Payment recorded.'}
                    </>
                  )}
                </p>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Actions */}
        <div className="flex flex-col sm:flex-row gap-4">
          <Button
            onClick={() => navigate('/menu')}
            variant="outline"
            className="flex-1"
            size="lg"
          >
            Continue Shopping
          </Button>
          <Button
            onClick={() => navigate('/')}
            className="flex-1 bg-neutral-950 hover:bg-black"
            size="lg"
          >
            Back to Home
          </Button>
        </div>

        {/* Confirmation Email Notice */}
        {order.customerEmail && (
          <div className="mt-8 text-center">
            <p className="text-gray-600">
              A confirmation email will be sent to {order.customerEmail}.
            </p>
          </div>
        )}
      </div>
    </div>
  );
};

export default OrderConfirmationPage;
